import { useState, useEffect } from 'react';
import { EngraveSettings, TextAlignment, HorizontalAlignment, VerticalAlignment, ValidationResult, ZoomLevel, ZOOM_SCALE_VALUE } from './types';
import { EngravePreview } from './components/EngravePreview';
import { TextInput } from './components/TextInput';
import { GoogleFormSubmit } from './components/GoogleFormSubmit';
import { ClipboardService } from './services/clipboardService';
import './App.css';

type TabType = 'front' | 'back';

// LocalStorageのキー
const STORAGE_KEY_FRONT = 'engraview_front_settings';
const STORAGE_KEY_BACK = 'engraview_back_settings';
const STORAGE_KEY_TAB = 'engraview_current_tab';
const STORAGE_KEY_ZOOM = 'engraview_zoom_level';
const STORAGE_KEY_FRONT_PRESET = 'engraview_front_preset_id';
const STORAGE_KEY_BACK_PRESET = 'engraview_back_preset_id';

// デフォルト設定
const getDefaultSettings = (): EngraveSettings => ({
  text: '',
  fontFamily: 'Noto Sans JP',
  fontSize: 12,
  alignment: TextAlignment.Center,
  isBold: false,
  isItalic: false,
  isVertical: false,
  horizontalAlignment: HorizontalAlignment.Center,
  verticalAlignment: VerticalAlignment.Center
});

/**
 * LocalStorageから設定を読み込む
 */
const loadSettingsFromStorage = (key: string): EngraveSettings | null => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('LocalStorageからの読み込みに失敗しました:', error);
  }
  return null;
};

/**
 * LocalStorageに設定を保存
 */
const saveSettingsToStorage = (key: string, settings: EngraveSettings): void => {
  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch (error) {
    console.error('LocalStorageへの保存に失敗しました:', error);
  }
};

/**
 * メインアプリケーションコンポーネント
 */
function App() {
  // 初回ロード時にLocalStorageから復元
  const [frontSettings, setFrontSettings] = useState<EngraveSettings>(() => {
    return loadSettingsFromStorage(STORAGE_KEY_FRONT) || getDefaultSettings();
  });
  const [backSettings, setBackSettings] = useState<EngraveSettings>(() => {
    return loadSettingsFromStorage(STORAGE_KEY_BACK) || getDefaultSettings();
  });
  const [currentTab, setCurrentTab] = useState<TabType>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_TAB);
    return (saved === 'back' ? 'back' : 'front') as TabType;
  });
  const [frontValidation, setFrontValidation] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    warnings: []
  });
  const [backValidation, setBackValidation] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    warnings: []
  });
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_ZOOM);
    return (saved === String(ZOOM_SCALE_VALUE) ? ZOOM_SCALE_VALUE : 1) as ZoomLevel;
  });
  const [frontPresetId, setFrontPresetId] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_FRONT_PRESET) || '';
  });
  const [backPresetId, setBackPresetId] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY_BACK_PRESET) || '';
  });
  const [clipboardFeedback, setClipboardFeedback] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // frontSettingsが変更されたらLocalStorageに保存
  useEffect(() => {
    saveSettingsToStorage(STORAGE_KEY_FRONT, frontSettings);
  }, [frontSettings]);

  // backSettingsが変更されたらLocalStorageに保存
  useEffect(() => {
    saveSettingsToStorage(STORAGE_KEY_BACK, backSettings);
  }, [backSettings]);

  // currentTabが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_TAB, currentTab);
  }, [currentTab]);

  // zoomLevelが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_ZOOM, zoomLevel.toString());
  }, [zoomLevel]);

  // frontPresetIdが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_FRONT_PRESET, frontPresetId);
  }, [frontPresetId]);

  // backPresetIdが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_BACK_PRESET, backPresetId);
  }, [backPresetId]);

  // Ctrl+Lでクリップボードから読み込み
  useEffect(() => {
    const clipboardService = new ClipboardService();

    const handleKeyDown = async (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        event.preventDefault();

        const result = await clipboardService.readAndParseFromClipboard();

        if (result.success && result.settings) {
          if (currentTab === 'front') {
            setFrontSettings(result.settings);
          } else {
            setBackSettings(result.settings);
          }

          setClipboardFeedback({ type: 'success', message: result.message });
          setTimeout(() => setClipboardFeedback({ type: null, message: '' }), 3000);
        } else {
          setClipboardFeedback({ type: 'error', message: `${result.error}: ${result.message}` });
          setTimeout(() => setClipboardFeedback({ type: null, message: '' }), 5000);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTab]);

  const handleSettingsChange = (newSettings: EngraveSettings) => {
    if (currentTab === 'front') {
      setFrontSettings(newSettings);
    } else {
      setBackSettings(newSettings);
    }
  };

  const handleLoadFromClipboard = async () => {
    const clipboardService = new ClipboardService();
    const result = await clipboardService.readAndParseFromClipboard();

    if (result.success && result.settings) {
      handleSettingsChange(result.settings);
      setClipboardFeedback({ type: 'success', message: result.message });
      setTimeout(() => setClipboardFeedback({ type: null, message: '' }), 3000);
    } else {
      setClipboardFeedback({ type: 'error', message: `${result.error}: ${result.message}` });
      setTimeout(() => setClipboardFeedback({ type: null, message: '' }), 5000);
    }
  };

  // 現在のタブに応じた設定を取得（プレビュー用）
  const getCurrentDisplaySettings = (): EngraveSettings => {
    return currentTab === 'front' ? frontSettings : backSettings;
  };

  // Googleフォームを開けるかどうかを判定
  const canSubmitForm = (): boolean => {
    // 前面のテキストまたは画像が入力されているか
    const hasFrontContent = frontSettings.isImageMode ? !!frontSettings.imageData : (!!frontSettings.text && frontSettings.text.trim().length > 0);
    // 後面のテキストまたは画像が入力されているか
    const hasBackContent = backSettings.isImageMode ? !!backSettings.imageData : (!!backSettings.text && backSettings.text.trim().length > 0);

    // 前面または後面のいずれかにコンテンツがあるか
    if (!hasFrontContent && !hasBackContent) {
      return false;
    }

    // 前面にコンテンツがある場合、バリデーションをチェック
    if (hasFrontContent && !frontValidation.isValid) {
      return false;
    }

    // 後面にコンテンツがある場合、バリデーションをチェック
    if (hasBackContent && !backValidation.isValid) {
      return false;
    }

    return true;
  };

  return (
    <div className="container mt-4">
      <h1 className="mb-4">バターストラップ 文字・画像入れのプレビュー</h1>

      {/* クリップボード読み込みフィードバック */}
      {clipboardFeedback.type && (
        <div className={`alert alert-${clipboardFeedback.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
          <i className={`bi bi-${clipboardFeedback.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
          {clipboardFeedback.message}
          <button type="button" className="btn-close" onClick={() => setClipboardFeedback({ type: null, message: '' })}></button>
        </div>
      )}

      {/* タブ切り替え */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${currentTab === 'front' ? 'active' : ''}`}
            onClick={() => setCurrentTab('front')}
          >
            前面
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${currentTab === 'back' ? 'active'  : ''}`}
            onClick={() => setCurrentTab('back')}
          >
            後面
          </button>
        </li>
      </ul>

      <div className="row">
        <div className="col-md-6 mb-4">
          <TextInput
            settings={getCurrentDisplaySettings()}
            onSettingsChange={handleSettingsChange}
            isFrontFace={currentTab === 'front'}
            onLoadFromClipboard={handleLoadFromClipboard}
            presetId={currentTab === 'front' ? frontPresetId : backPresetId}
            onPresetChange={(id) => currentTab === 'front' ? setFrontPresetId(id) : setBackPresetId(id)}
          />
        </div>
        <div className="col-md-6 mb-4">
          {/* ズーム切り替えボタン */}
          <div className="mb-3 d-flex justify-content-center">
            <div className="btn-group" role="group" aria-label="ズーム切り替え">
              <button
                type="button"
                className={`btn ${zoomLevel === 1 ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setZoomLevel(1)}
              >
                ほぼ実物大
              </button>
              <button
                type="button"
                className={`btn ${zoomLevel === ZOOM_SCALE_VALUE ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setZoomLevel(ZOOM_SCALE_VALUE)}
              >
                拡大
              </button>
            </div>
          </div>

          {/* 前面プレビュー - 常にレンダリングして検証を実行 */}
          <div style={{ display: currentTab === 'front' ? 'block' : 'none' }}>
            <EngravePreview
              settings={frontSettings}
              isFrontFace={true}
              zoomLevel={zoomLevel}
              onValidationChange={(result) => setFrontValidation(result)}
            />
          </div>

          {/* 後面プレビュー - 常にレンダリングして検証を実行 */}
          <div style={{ display: currentTab === 'back' ? 'block' : 'none' }}>
            <EngravePreview
              settings={backSettings}
              isFrontFace={false}
              zoomLevel={zoomLevel}
              onValidationChange={(result) => setBackValidation(result)}
            />
          </div>
        </div>
      </div>

      {/* エラー表示（タブ切り替えの範囲外） */}
      <div className="row mt-3">
        <div className="col-md-12">
          {(() => {
            const hasFrontContent = frontSettings.isImageMode ? !!frontSettings.imageData : (!!frontSettings.text && frontSettings.text.trim().length > 0);
            const hasBackContent = backSettings.isImageMode ? !!backSettings.imageData : (!!backSettings.text && backSettings.text.trim().length > 0);

            // 前面と後面の両方が空の場合
            if (!hasFrontContent && !hasBackContent) {
              return (
                <div className="alert alert-warning">
                  前面と後面のどちらかを入力してください
                </div>
              );
            }

            // 前面のみ空の場合
            if (!hasFrontContent && hasBackContent) {
              return (
                <div className="alert alert-info">
                  <strong>前面:</strong> 前面は未入力です。後面のみで送信します。
                </div>
              );
            }

            // 後面のみ空の場合
            if (hasFrontContent && !hasBackContent) {
              return (
                <div className="alert alert-info">
                  <strong>後面:</strong> 後面は未入力です。前面のみで送信します。
                </div>
              );
            }

            return null;
          })()}

          {/* 前面のバリデーションエラー */}
          {frontSettings.text && frontSettings.text.trim().length > 0 && !frontValidation.isValid && frontValidation.errors.length > 0 && (
            <div className="alert alert-danger">
              <strong>前面:</strong> エラーがあります
              <ul className="mb-0 mt-2 list-unstyled">
                {frontValidation.errors.map((error, index) => (
                  <li key={index}>
                    <i className="bi bi-x-circle-fill text-danger me-2"></i>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 後面のバリデーションエラー */}
          {backSettings.text && backSettings.text.trim().length > 0 && !backValidation.isValid && backValidation.errors.length > 0 && (
            <div className="alert alert-danger">
              <strong>後面:</strong> エラーがあります
              <ul className="mb-0 mt-2 list-unstyled">
                {backValidation.errors.map((error, index) => (
                  <li key={index}>
                    <i className="bi bi-x-circle-fill text-danger me-2"></i>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {(() => {
        const hasFrontContent = frontSettings.isImageMode ? !!frontSettings.imageData : (!!frontSettings.text && frontSettings.text.trim().length > 0);
        const hasBackContent = backSettings.isImageMode ? !!backSettings.imageData : (!!backSettings.text && backSettings.text.trim().length > 0);

        return (hasFrontContent || hasBackContent) ? (
          <div className="row mt-4">
            <div className="col-md-12">
              <hr />
              <GoogleFormSubmit
                frontSettings={frontSettings}
                backSettings={backSettings}
                canSubmit={canSubmitForm()}
              />
            </div>
          </div>
        ) : null;
      })()}

      {/* ビルド情報 */}
      <div className="row mt-5">
        <div className="col-md-12 text-center">
          <small className="text-muted">
            Build: {__GIT_COMMIT_HASH__} ({__GIT_COMMIT_DATE__})
          </small>
        </div>
      </div>
    </div>
  );
}

export default App;
